<template>
    <v-card
            ref="drawing-dialog"
            class="drawing-card"
            :style="cardStyles">
        <v-toolbar
                class="annotation-floating-toolbar"
                dense
                floating>
            <DrawingBrushMenu v-model="brush" />
            <DrawingUndo
                    :available="undoAvailable"
                    @undo="undoDrawing" />
            <DrawingRedo
                    :available="redoAvailable"
                    @redo="redoDrawing" />
            <v-btn
                    icon
                    @touchstart="closeDrawArea"
                    @click="closeDrawArea">
                <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-toolbar>
        <div
                ref="drawing-container"
                class="drawing-container-div"
                :style="containerStyle">
            <canvas ref="drawing-canvas" />
        </div>
    </v-card>
</template>

<script>
    import {fabric} from 'fabric';
    import DrawingBrushMenu from "@/views/Form/components/components/components/components/DrawingBrushMenu.vue";
    import DrawingUndo from "@/views/Form/components/components/components/components/DrawingUndo.vue";
    import DrawingRedo from "@/views/Form/components/components/components/components/DrawingRedo.vue";

    export default {
        name: "DrawingFullScreenView",
        components: {DrawingRedo, DrawingUndo, DrawingBrushMenu},
        props: {
            open: {type: Boolean, default: false},
            defaultColor: {type: String, default: () => "#000"},
            width: {type: Number, required: true},
            height: {type: Number, required: true},
            bgSrc: {type: String, required: true},
        },
        data() {
            return {
                $drawing: null,
                $drawingCursor: null,
                refresh: 0,
                itemsNumber: 0,
                redoStack: [],
                undoRedoProcessing: false,
                openDrawingArea: false,
                openedBefore: false,
                prevContainerWidth: 0,
                containerWidth: 0,
                prevContainerHeight: 0,
                containerHeight: 0,
                brush: {
                    color: this.defaultColor,
                    size: 1
                },
                value: '',
                redoStrokesHistory: [],

            }
        },
        computed: {
            cardStyles() {
                return {
                    display: this.openDrawingArea ? 'flex' : 'none',
                    'vertical-align': 'middle',
                    'flex-direction': 'column'
                }
            },
            undoAvailable() {
                return this.itemsNumber > 0;
            },
            redoAvailable() {
                return this.redoStack.length > 0 || false;
            },
            containerStyle() {
                this.refresh;

                let width = 0;
                let height = 0;

                const maxWidth = window.innerWidth;
                const maxHeight = window.innerHeight - 60;  // 60 is a height of a toolbar
                const widthByHeight = this.width * maxHeight / this.height;
                const heightByWidth = this.height * maxWidth / this.width;

                if (widthByHeight <= maxWidth) {
                    width = widthByHeight;
                    height = maxHeight;
                } else {
                    width = maxWidth;
                    height = heightByWidth;
                }

                return {
                    width: width + 'px',
                    height: height + 'px',
                    margin: 'auto auto',
                }
            },
        },
        watch: {
            brush: {
                handler(updatedBrush) {
                    this.$drawing.freeDrawingBrush.color = updatedBrush.color
                    this.$drawing.freeDrawingBrush.width = updatedBrush.size;
                },
                deep: true,
            },
            open(val) {
                if (val) {
                    this.openDrawArea()
                }
            }
        },
        methods: {
            init() {
                fabric.Point.prototype.lerp = function (that, t) {
                    if (typeof t === 'undefined') {
                        // We override it to change this t from 0.5 to 1
                        // With 0.5 (default by lib) on Mobile Safari drawing lines are breaking (dotted)
                        t = 1;
                    }
                    t = Math.max(Math.min(1, t), 0);
                    return new fabric.Point(this.x + (that.x - this.x) * t, this.y + (that.y - this.y) * t);
                }

                this.$drawing = new fabric.Canvas(this.$refs["drawing-canvas"], {
                    isDrawingMode: true,
                });

                this.$drawing.freeDrawingBrush.width = this.brush.size;
                this.$drawing.freeDrawingBrush.color = this.brush.color;

                this.$drawing.on('object:added', () => {
                    if (!this.undoRedoProcessing) {
                        this.redoStack = [];
                    }
                    this.itemsNumber = this.$drawing.getObjects().length;

                });

                this.$drawing.on('object:removed', () => {
                    this.itemsNumber = this.$drawing.getObjects().length;
                });

                this.openedBefore = true;
            },
            onResize(close = false) {
                this.refresh++;
                this.$nextTick(() => {
                    const scale = parseInt(this.containerStyle.width, 10) / this.$drawing.getWidth();
                    this.$drawing.getObjects().forEach(object => {
                        return this.resizeObject(object, scale)
                    })
                    this.redoStack.forEach(object => {
                        return this.resizeObject(object, scale)
                    })
                    if (close === true) {
                        this.$nextTick(() => {
                            this.closeDrawArea()
                        })
                    } else {
                        this.renderDrawing()
                    }
                })
            },
            resizeObject(object, scale) {
                object.scaleX = object.scaleX * scale
                object.scaleY = object.scaleY * scale
                object.left = object.left * scale
                object.top = object.top * scale
                object.setCoords();
                return object
            },
            addEventListeners() {
                const self = this;
                window.addEventListener('resize', this.onResize);
                document.addEventListener("webkitfullscreenchange", function () {
                    if (!document.fullscreenElement) {
                        self.closeDrawArea();
                    }
                }, false);
                document.addEventListener("fullscreenchange", function () {
                    if (!document.webkitIsFullScreen) {
                        self.closeDrawArea();
                    }
                }, false);
                document.addEventListener("mozfullscreenchange", function () {
                    if (!document.mozFullScreen) {
                        self.closeDrawArea();
                    }
                }, false);
            },
            removeEventListeners() {
                window.removeEventListener('resize', this.onResize);
            },
            triggerContainerRestyle() {
                this.refresh++;
            },
            async openDrawArea() {
                this.openDrawingArea = true;

                this.$nextTick(() => {
                    var canvas = this.$refs['drawing-dialog'].$el;

                    if (canvas.requestFullScreen) {
                        canvas.requestFullScreen();
                    } else if (canvas.webkitRequestFullScreen) {
                        alert(51);
                        canvas.webkitRequestFullScreen();
                    } else if (canvas.msRequestFullscreen) {
                        canvas.msRequestFullscreen();
                    } else if (canvas.mozRequestFullScreen) {
                        canvas.mozRequestFullScreen();
                    }

                    if (!this.openedBefore) {
                        this.init();
                    }

                    this.addEventListeners()
                    this.triggerContainerRestyle();

                    this.$nextTick(() => {
                        this.renderDrawing()
                    })
                })
            },
            async closeDrawArea() {
                this.triggerContainerRestyle();
                await this.renderDrawing();
                this.value = await this.getValue()

                this.openDrawingArea = false;

                if (document.fullscreenElement && document.exitFullscreen) {
                    document.exitFullscreen();
                } else if (document.webkitIsFullScreen && document.webkitCancelFullScreen) {
                    document.webkitCancelFullScreen();
                } else if (document.msFullScreen && document.msExitFullscreen) {
                    document.msExitFullscreen();
                } else if (document.mozFullScreen && document.mozExitFullscreen) {
                    document.mozExitFullscreen();
                }

                this.$nextTick(() => {
                    this.$nextTick(() => {
                        this.$emit('closed', this.value);
                        this.removeEventListeners()
                    })
                })
            },
            redoDrawing() {
                this.undoRedoProcessing = true;
                if (this.redoStack.length > 0) {
                    let item = this.redoStack.pop();
                    this.$drawing.add(item);
                }
                this.undoRedoProcessing = false;
            },
            undoDrawing() {
                this.undoRedoProcessing = true;
                if (this.itemsNumber > 0) {
                    let lastItem = this.$drawing.item(this.$drawing.getObjects().length - 1);
                    this.redoStack.push(lastItem);
                    this.$drawing.remove(lastItem);
                }
                this.undoRedoProcessing = false;
            },
            async renderDrawing(state = null) {
                this.$drawing.setHeight(parseInt(this.containerStyle.height, 10));
                this.$drawing.setWidth(parseInt(this.containerStyle.width, 10));
                this.$drawing.calcOffset();
                this.$drawing.setBackgroundImage(
                    this.bgSrc,
                    this.$drawing.renderAll.bind(this.$drawing),
                    {
                        crossOrigin: 'anonymous',
                        scaleX: parseInt(this.containerStyle.width, 10) / this.width,
                        scaleY: parseInt(this.containerStyle.height, 10) / this.height
                    }
                );

                if (state !== null) {
                    this.$drawing.loadFromJSON(state, () => {
                        this.$drawing.renderAll();
                    })
                } else {
                    this.$drawing.renderAll();
                }
                this.$refs["drawing-canvas"].$el.addEventListener('touchstart', function (e) {
                    e.preventDefault();
                    e.stopPropagation();
                })
            },

            async getValue() {
                return await this.$drawing.toDataURL();
            },
        }
    }

</script>

<style>

canvas {
  user-select: none !important;
}

.drawing-container__error {
  border: 1px solid red !important;
}

.drawing-card {
  background-image: linear-gradient(45deg, #ffffff 45.45%, #e3e3e3 45.45%, #e3e3e3 50%, #ffffff 50%, #ffffff 95.45%, #e3e3e3 95.45%, #e3e3e3 100%);
  background-size: 15.56px 15.56px;
  background-repeat: repeat;
  touch-action: manipulation !important;
}

.drawing-card-active {
  margin: 0 auto !important;
}

.annotation-floating-toolbar {
  position: fixed;
  bottom: 5%;
  right: 5%;
  z-index: 100500;
}

</style>